import { Entity } from '@api/api/entity-schema';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { BrandAndProductDrawer } from '@terminal/modules/brand-and-product-drawer/BrandAndProductDrawer';
import { useCompanyCompetitors } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { NewsView } from '@terminal/views/news/components/news-view/NewsView';
import { ThemeEarningsDrawer } from '@terminal/views/theme-overview/tab-content/theme-earnings/ThemeEarningsDrawer';
import { useThemeEarningsSummaryDrawer } from '@terminal/views/theme-overview/tab-content/theme-earnings/useThemeEarningsSummaryDrawer';
import { AssetFinancialsWidget } from '@terminal/widgets/asset-financials-widget/AssetFinancialsWidget';
import { ChartPeerGroup } from '@terminal/widgets/chart-peer-group/ChartPeerGroup';
import { Document } from '@terminal/widgets/document-widget/DocumentWidget';
import { Earnings } from '@terminal/widgets/earnings/Earnings';
import { CompetitorsDrawer } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/CompetitorsDrawer';
import { GeographicExposureDrawer } from '@terminal/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/GeographicExposureDrawer';
import { KnowledgeGraphWidget } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { LegacyOverview } from '@terminal/widgets/overview/LegacyOverview';
import { Overview } from '@terminal/widgets/overview/Overview';
import { PEER_GROUP_DISPLAYED } from '@terminal/widgets/overview/utils';
import { ThemeDrawer } from '@terminal/widgets/theme-widget/theme-drawer/ThemeDrawer';
import { TabsType } from './types';

export interface AssetOverviewTabsProps {
  entity: Entity;
  activeTab: TabsType;
}

export const TabContent = ({ entity, activeTab }: AssetOverviewTabsProps) => {
  const { drawerProps: themeEarningsDrawerProps } =
    useThemeEarningsSummaryDrawer();
  const isAoPulseEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5199-ao-pulse')
  );
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const { competitorsData, isFetching } = useCompanyCompetitors({
    entityTag: entity.tag,
    limit: PEER_GROUP_DISPLAYED,
  });
  const peerEntities = competitorsData.map(s => s.entity);

  const renderElement = () => {
    switch (activeTab) {
      case TabsType.Financials: {
        return <AssetFinancialsWidget entity={entity} />;
      }

      case TabsType.Chart: {
        return (
          <ChartPeerGroup
            entity={entity}
            peerEntities={peerEntities}
            isFetching={isFetching}
          />
        );
      }

      case TabsType.Connections: {
        return <KnowledgeGraphWidget entity={entity} />;
      }

      case TabsType.NewsInsights: {
        return (
          <NewsView
            entity={entity}
            includedFilterKeys={['providers']}
            trackingSource={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
          />
        );
      }

      case TabsType.Documents: {
        return (
          <ErrorBoundaryWrapper showBorder={false}>
            <Document entityTag={entity.tag} />
          </ErrorBoundaryWrapper>
        );
      }

      case TabsType.Earnings: {
        return <Earnings entity={entity} />;
      }

      case TabsType.Overview:

      default: {
        return isAoPulseEnabled && !isBasicUser ? (
          <Overview entity={entity} key={entity.tag} />
        ) : (
          <LegacyOverview entity={entity} key={entity.tag} />
        );
      }
    }
  };

  return (
    <>
      <CompetitorsDrawer entity={entity} />
      <ThemeDrawer entity={entity} />
      <GeographicExposureDrawer entity={entity} />
      <BrandAndProductDrawer entity={entity} />
      <ThemeEarningsDrawer {...themeEarningsDrawerProps} />
      {renderElement()}
    </>
  );
};
